/**
 * The payer-facing payment card. Used live on /pay/:unitId/:linkId and, with `preview`,
 * as the live preview inside the create/edit form so units see exactly what payers see.
 */

import { useEffect, useMemo, useState } from 'react';
import { Gift, Loader2, Lock, User, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { PublicPaymentLinkResponse } from '@masonic-tools/shared-types';
import {
  canOfferGiftAid,
  computeFeeBreakdown,
  effectiveMinimumPence,
  fillGiftAidDeclaration,
  formatPence,
  formatPenceShort,
  giftAidValuePence,
  MESSAGE_MAX_LENGTH,
  METHOD_PROVIDER_NAMES,
  parsePoundsInput,
  penceToInput,
  splitName,
  validatePayerForm,
  type FormErrors,
  type PayerFormInput,
} from '@/lib/unit-payment-links';
import { ReferenceChip } from './PaymentLinkStatusBadge';

export type PublicLink = PublicPaymentLinkResponse['link'];

export const EMPTY_PAYER_FORM: PayerFormInput = {
  amountPence: null,
  giveAnonymously: true,
  payerName: '',
  payerEmail: '',
  message: '',
  giftAid: false,
  giftAidTitle: '',
  giftAidFirstName: '',
  giftAidLastName: '',
  giftAidHouse: '',
  giftAidPostcode: '',
  giftAidConfirmed: false,
};

interface Props {
  unitName: string;
  logoUrl?: string | null;
  link: PublicLink;
  giftAidTemplate: string;
  /** Preview inside the form: inputs work but the pay button does nothing. */
  preview?: boolean;
  submitting?: boolean;
  initialForm?: Partial<PayerFormInput>;
  serverError?: { message: string; field?: string } | null;
  onSubmit?: (form: PayerFormInput) => void;
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-sm text-destructive" role="alert">
      {message}
    </p>
  );
}

const SERVER_FIELD_MAP: Record<string, string> = {
  amountPence: 'amount',
  'giftAid.firstName': 'giftAidFirstName',
  'giftAid.lastName': 'giftAidLastName',
  'giftAid.houseNameOrNumber': 'giftAidHouse',
  'giftAid.postcode': 'giftAidPostcode',
  'giftAid.confirmed': 'giftAidConfirmed',
};

export function PublicPaymentLinkCard({
  unitName,
  logoUrl,
  link,
  giftAidTemplate,
  preview = false,
  submitting = false,
  initialForm,
  serverError,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<PayerFormInput>(() => ({
    ...EMPTY_PAYER_FORM,
    amountPence: link.amountType === 'fixed' ? link.fixedAmountPence : null,
    ...initialForm,
  }));
  const [amountText, setAmountText] = useState(() => penceToInput(initialForm?.amountPence ?? null));
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Keep the fixed amount in step with the form preview.
  useEffect(() => {
    if (link.amountType === 'fixed') setForm((f) => ({ ...f, amountPence: link.fixedAmountPence }));
  }, [link.amountType, link.fixedAmountPence]);

  const rules = {
    amountType: link.amountType,
    fixedAmountPence: link.fixedAmountPence,
    minAmountPence: link.minAmountPence,
    maxAmountPence: link.maxAmountPence,
    feeMode: link.feeMode,
    feePence: link.feePence,
    giftAidEnabled: link.giftAidEnabled,
  };
  const errors: FormErrors = useMemo(() => validatePayerForm(rules, form), [form, link]); // eslint-disable-line react-hooks/exhaustive-deps
  const giftAidAvailable = canOfferGiftAid(rules, form);
  const amount = form.amountPence;
  const breakdown = amount !== null && !errors.amount ? computeFeeBreakdown(link.feeMode, amount, link.feePence) : null;

  const serverField = serverError?.field ? SERVER_FIELD_MAP[serverError.field] || serverError.field : undefined;
  const show = (field: string) => (submitAttempted || touched[field] ? errors[field] : undefined) || (serverField === field ? serverError?.message : undefined);

  const set = <K extends keyof PayerFormInput>(key: K, value: PayerFormInput[K]) => setForm((f) => ({ ...f, [key]: value }));

  const chooseAmount = (pence: number) => {
    set('amountPence', pence);
    setAmountText(penceToInput(pence));
    setTouched((t) => ({ ...t, amount: true }));
  };

  const toggleGiftAid = (on: boolean) => {
    setForm((f) => {
      if (!on) return { ...f, giftAid: false };
      const { firstName, lastName } = splitName(f.payerName);
      return {
        ...f,
        giftAid: true,
        giftAidFirstName: f.giftAidFirstName || firstName,
        giftAidLastName: f.giftAidLastName || lastName,
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (preview || submitting) return;
    const current = validatePayerForm(rules, form);
    if (Object.keys(current).length > 0) return;
    onSubmit?.({ ...form, giftAid: form.giftAid && giftAidAvailable });
  };

  const payLabel = (() => {
    const total = breakdown ? formatPence(breakdown.totalChargedPence) : '';
    const verb = link.paymentMethod === 'slickpay' ? 'Pay by bank' : 'Pay with card';
    return total ? `${verb} · ${total}` : verb;
  })();

  const suggestions = link.amountType === 'fixed' ? [] : link.suggestedAmountsPence;
  const declaration = fillGiftAidDeclaration(giftAidTemplate, amount ?? 0, unitName || 'the unit');

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      {/* Header */}
      <div className="space-y-2 border-b border-border p-5 sm:p-6">
        <div className="flex items-center gap-3">
          {logoUrl ? <img src={logoUrl} alt="" className="h-10 w-10 rounded-full object-cover" /> : null}
          <p className="text-sm font-medium text-muted-foreground">{unitName}</p>
        </div>
        <h1 className="break-words text-2xl font-bold leading-tight">{link.title || 'Untitled payment link'}</h1>
        {link.description ? <p className="whitespace-pre-line break-words text-sm text-muted-foreground">{link.description}</p> : null}
        {link.reference ? (
          <p className="text-xs text-muted-foreground">
            Reference: <ReferenceChip reference={link.reference} />
          </p>
        ) : null}
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        {/* Amount */}
        <section aria-labelledby="pl-amount-heading" className="space-y-3">
          <h2 id="pl-amount-heading" className="text-sm font-semibold">
            Amount
          </h2>
          {link.amountType === 'fixed' ? (
            <p className="text-4xl font-bold tabular-nums">{formatPence(link.fixedAmountPence)}</p>
          ) : (
            <>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground">£</span>
                <Input
                  id="pl-amount"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Amount in pounds"
                  aria-invalid={!!show('amount')}
                  aria-describedby="pl-amount-help pl-amount-error"
                  className="h-14 pl-8 text-2xl font-semibold tabular-nums"
                  placeholder="0.00"
                  value={amountText}
                  onChange={(e) => {
                    setAmountText(e.target.value);
                    set('amountPence', parsePoundsInput(e.target.value));
                  }}
                  onBlur={() => setTouched((t) => ({ ...t, amount: true }))}
                />
              </div>
              <p id="pl-amount-help" className="text-xs text-muted-foreground">
                {link.amountType === 'range'
                  ? `Between ${formatPence(effectiveMinimumPence(rules))} and ${formatPence(link.maxAmountPence)}`
                  : `Enter any amount from ${formatPence(effectiveMinimumPence(rules))}`}
              </p>
              {suggestions.length > 0 ? (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Suggested amounts">
                  {suggestions.map((p) => (
                    <Button
                      key={p}
                      type="button"
                      variant={amount === p ? 'default' : 'outline'}
                      className="min-h-[44px] min-w-[64px]"
                      onClick={() => chooseAmount(p)}
                    >
                      {formatPenceShort(p)}
                    </Button>
                  ))}
                </div>
              ) : null}
              <FieldError id="pl-amount-error" message={show('amount')} />
            </>
          )}
        </section>

        {/* Fee breakdown */}
        <section aria-live="polite" className="rounded-lg bg-muted/60 p-4 text-sm">
          {breakdown ? (
            link.feeMode === 'on_top' ? (
              <dl className="space-y-1">
                <div className="flex justify-between">
                  <dt>Payment</dt>
                  <dd className="tabular-nums">{formatPence(breakdown.amountPence)}</dd>
                </div>
                {breakdown.processingFeePence > 0 ? (
                  <div className="flex justify-between text-muted-foreground">
                    <dt>Processing fee</dt>
                    <dd className="tabular-nums">{formatPence(breakdown.processingFeePence)}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between border-t border-border pt-1 font-semibold">
                  <dt>Total</dt>
                  <dd className="tabular-nums">{formatPence(breakdown.totalChargedPence)}</dd>
                </div>
              </dl>
            ) : (
              <p>
                <strong>You pay {formatPence(breakdown.totalChargedPence)}</strong>
                {breakdown.processingFeePence > 0 ? (
                  <span className="text-muted-foreground">
                    {' '}
                    · {formatPence(breakdown.netToUnitPence)} goes to {unitName || 'the unit'} after a {formatPence(breakdown.processingFeePence)} processing fee
                  </span>
                ) : null}
              </p>
            )
          ) : (
            <p className="text-muted-foreground">Enter an amount to see the total.</p>
          )}
        </section>

        {/* Your details */}
        <section aria-labelledby="pl-details-heading" className="space-y-3">
          <h2 id="pl-details-heading" className="text-sm font-semibold">
            Your details
          </h2>
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-1" role="radiogroup" aria-label="Give anonymously or add your details">
            {[
              { value: true, label: 'Give anonymously', icon: UserX },
              { value: false, label: 'Add my name & email', icon: User },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={form.giveAnonymously === value}
                onClick={() => setForm((f) => ({ ...f, giveAnonymously: value, giftAid: value ? false : f.giftAid }))}
                className={cn(
                  'flex min-h-[44px] items-center justify-center gap-2 rounded-md px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  form.giveAnonymously === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>

          {!form.giveAnonymously ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pl-name">Name</Label>
                <Input
                  id="pl-name"
                  autoComplete="name"
                  value={form.payerName}
                  aria-invalid={!!show('payerName')}
                  onChange={(e) => set('payerName', e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, payerName: true }))}
                />
                <FieldError id="pl-name-error" message={show('payerName')} />
              </div>
              <div>
                <Label htmlFor="pl-email">Email</Label>
                <Input
                  id="pl-email"
                  type="email"
                  autoComplete="email"
                  value={form.payerEmail}
                  aria-invalid={!!show('payerEmail')}
                  aria-describedby="pl-email-help"
                  onChange={(e) => set('payerEmail', e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, payerEmail: true }))}
                />
                <p id="pl-email-help" className="mt-1 text-xs text-muted-foreground">
                  Your email is only used to send your receipt.
                </p>
                <FieldError id="pl-email-error" message={show('payerEmail')} />
              </div>
            </div>
          ) : null}

          <div>
            <Label htmlFor="pl-message">
              Message to {unitName || 'the unit'} <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="pl-message"
              rows={2}
              maxLength={MESSAGE_MAX_LENGTH}
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
            />
            <p className="mt-1 text-right text-xs text-muted-foreground">
              {form.message.length} / {MESSAGE_MAX_LENGTH}
            </p>
          </div>
        </section>

        {/* Gift Aid */}
        {link.giftAidEnabled && giftAidAvailable ? (
          <section className="space-y-3 rounded-lg border-2 border-primary/30 bg-primary/5 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[hsl(var(--primary))]"
                checked={form.giftAid}
                onChange={(e) => toggleGiftAid(e.target.checked)}
              />
              <span>
                <span className="flex items-center gap-2 font-semibold">
                  <Gift className="h-4 w-4" /> Add Gift Aid
                  {amount ? <span className="font-normal text-primary">+{formatPence(giftAidValuePence(amount))}</span> : null}
                </span>
                <span className="block text-sm text-muted-foreground">
                  Boost your donation by 25p of Gift Aid for every £1 you donate, at no extra cost to you.
                </span>
              </span>
            </label>

            {form.giftAid ? (
              <div className="space-y-3 pt-1">
                <div className="grid grid-cols-[80px_1fr] gap-2 sm:grid-cols-[80px_1fr_1fr]">
                  <div>
                    <Label htmlFor="pl-ga-title">Title</Label>
                    <Input id="pl-ga-title" autoComplete="honorific-prefix" maxLength={4} value={form.giftAidTitle} onChange={(e) => set('giftAidTitle', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="pl-ga-first">First name</Label>
                    <Input
                      id="pl-ga-first"
                      autoComplete="given-name"
                      value={form.giftAidFirstName}
                      aria-invalid={!!show('giftAidFirstName')}
                      onChange={(e) => set('giftAidFirstName', e.target.value)}
                    />
                    <FieldError id="pl-ga-first-error" message={show('giftAidFirstName')} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="pl-ga-last">Last name</Label>
                    <Input
                      id="pl-ga-last"
                      autoComplete="family-name"
                      value={form.giftAidLastName}
                      aria-invalid={!!show('giftAidLastName')}
                      onChange={(e) => set('giftAidLastName', e.target.value)}
                    />
                    <FieldError id="pl-ga-last-error" message={show('giftAidLastName')} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="pl-ga-house">House name or number</Label>
                    <Input
                      id="pl-ga-house"
                      autoComplete="address-line1"
                      value={form.giftAidHouse}
                      aria-invalid={!!show('giftAidHouse')}
                      onChange={(e) => set('giftAidHouse', e.target.value)}
                    />
                    <FieldError id="pl-ga-house-error" message={show('giftAidHouse')} />
                  </div>
                  <div>
                    <Label htmlFor="pl-ga-postcode">Postcode</Label>
                    <Input
                      id="pl-ga-postcode"
                      autoComplete="postal-code"
                      className="uppercase"
                      value={form.giftAidPostcode}
                      aria-invalid={!!show('giftAidPostcode')}
                      onChange={(e) => set('giftAidPostcode', e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, giftAidPostcode: true }))}
                    />
                    <FieldError id="pl-ga-postcode-error" message={show('giftAidPostcode')} />
                  </div>
                </div>
                <p className="rounded-md bg-background p-3 text-xs leading-relaxed text-muted-foreground">{declaration}</p>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[hsl(var(--primary))]"
                    checked={form.giftAidConfirmed}
                    aria-invalid={!!show('giftAidConfirmed')}
                    onChange={(e) => set('giftAidConfirmed', e.target.checked)}
                  />
                  <span>I confirm I am a UK taxpayer and agree to the declaration above.</span>
                </label>
                <FieldError id="pl-ga-confirm-error" message={show('giftAidConfirmed')} />
              </div>
            ) : null}
          </section>
        ) : null}

        {serverError && !serverField ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
            {serverError.message}
          </p>
        ) : null}
      </div>

      {/* Pay button: sticky on phones */}
      <div className="sticky bottom-0 border-t border-border bg-card/95 p-4 backdrop-blur sm:static sm:bg-card sm:p-6 sm:pt-0 sm:backdrop-blur-none">
        <Button type="submit" size="lg" className="min-h-[48px] w-full text-base" disabled={submitting} aria-disabled={preview}>
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to secure payment…
            </>
          ) : (
            payLabel
          )}
        </Button>
        <p className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" /> Secure payment by {METHOD_PROVIDER_NAMES[link.paymentMethod]}
          {preview ? ' · preview only' : ''}
        </p>
      </div>
    </form>
  );
}
